import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Typography, Tabs, Tab, Button, TextField, useTheme, useMediaQuery } from "@mui/material";
import { Download } from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Atendimento from "../../components/Atendimento";
import VendaProdutos from "../../components/VendaProdutos";
import CustomerAwareness from "../../components/CustomerAwareness";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/pt-br";
import "dayjs/locale/en";
import "dayjs/locale/it";
import "dayjs/locale/es";
import Produtos from "../../components/Produtos";
import VendaPassagens from "../../components/VendaPassagens";
import SurveyAnalysis from "../../components/SurveyAnalysis";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import { useNavigate } from "react-router-dom";
import { getBillingOffers } from "../../api/client";

// >>> NOVO: helper para exportação CSV
import { exportDashboardCsv } from "../../api/client";

dayjs.extend(localizedFormat);
dayjs.locale("pt-br");

const STORAGE_KEY_START = "dashboard_start_date";
const STORAGE_KEY_END = "dashboard_end_date";

function getInitialStartDate() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_START);
    if (s) {
      const d = dayjs(s, "YYYY-MM-DD", true);
      if (d.isValid()) return d;
    }
  } catch (e) {}
  return dayjs().startOf("month");
}

function getInitialEndDate() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_END);
    if (s) {
      const d = dayjs(s, "YYYY-MM-DD", true);
      if (d.isValid()) return d;
    }
  } catch (e) {}
  return dayjs().endOf("month");
}

const Dashboard = () => {
  const { t, i18n } = useTranslation("settings");
  const navigate = useNavigate();
  const { segment } = useWorkspaceSegment();
  const [activeTab, setActiveTab] = React.useState(0);
  const [startDate, setStartDate] = React.useState(getInitialStartDate);
  const [endDate, setEndDate] = React.useState(getInitialEndDate);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detecta mobile
  const [billingSignal, setBillingSignal] = React.useState(null);

  React.useEffect(() => {
    const lng = (i18n.language || "pt-BR").toLowerCase();
    dayjs.locale(lng.startsWith("pt") ? "pt-br" : "en");
  }, [i18n.language]);

  React.useEffect(() => {
    let mounted = true;
    getBillingOffers({ autoGenerate: 1 })
      .then((res) => {
        if (!mounted) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        setBillingSignal(items[0] || null);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    const onLimitHit = (ev) => {
      const msg = ev?.detail?.message || "Seu plano atingiu um limite. Avalie upgrade ou adicional.";
      setBillingSignal({ payload: { message: msg } });
    };
    window.addEventListener("xpro:billing-limit-hit", onLimitHit);
    return () => window.removeEventListener("xpro:billing-limit-hit", onLimitHit);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleStartDateChange = (newValue) => {
    if (newValue) {
      const d = dayjs(newValue);
      setStartDate(d);
      try {
        localStorage.setItem(STORAGE_KEY_START, d.format("YYYY-MM-DD"));
      } catch (e) {}
    }
  };

  const handleEndDateChange = (newValue) => {
    if (newValue) {
      const d = dayjs(newValue);
      setEndDate(d);
      try {
        localStorage.setItem(STORAGE_KEY_END, d.format("YYYY-MM-DD"));
      } catch (e) {}
    }
  };

  const tabsDef = React.useMemo(() => {
    const base = [
      {
        key: "overview",
        label: t("dashboardPage.tabOverview"),
        section: "atendimento",
        render: () => (
          <Atendimento start={startDate.format("YYYY-MM-DD")} end={endDate.format("YYYY-MM-DD")} />
        ),
      },
      {
        key: "clients",
        label: t("dashboardPage.tabClients"),
        section: "clientes",
        render: () => (
          <CustomerAwareness start={startDate.format("YYYY-MM-DD")} end={endDate.format("YYYY-MM-DD")} />
        ),
      },
      {
        key: "products",
        label: t("dashboardPage.tabProducts"),
        section: "produtos",
        // No segmento de transporte rodoviário, "Produtos" vira "Passagens"
        segments: [WorkspaceSegments.Generic, WorkspaceSegments.AutoParts],
        render: () => (
          <Produtos start={startDate.format("YYYY-MM-DD")} end={endDate.format("YYYY-MM-DD")} />
        ),
      },
      {
        key: "sales",
        label: t("dashboardPage.tabSales"),
        section: "vendas",
        render: () => (
          <VendaProdutos start={startDate.format("YYYY-MM-DD")} end={endDate.format("YYYY-MM-DD")} />
        ),
      },
      {
        key: "busTickets",
        label: t("dashboardPage.tabBusTickets"),
        section: "venda-passagens",
        segments: [WorkspaceSegments.PassengerTransport],
        render: () => (
          <VendaPassagens start={startDate.format("YYYY-MM-DD")} end={endDate.format("YYYY-MM-DD")} />
        ),
      },
      {
        key: "surveys",
        label: t("dashboardPage.tabSurveys"),
        section: "surveys",
        render: () => (
          <SurveyAnalysis start={startDate.format("YYYY-MM-DD")} end={endDate.format("YYYY-MM-DD")} />
        ),
      },
    ];

    return base.filter((tab) => {
      if (Array.isArray(tab.segments) && tab.segments.length > 0) {
        return tab.segments.includes(segment);
      }
      return true;
    });
  }, [t, i18n.language, startDate, endDate, segment]);

  const sectionByTab = React.useMemo(() => {
    const m = {};
    tabsDef.forEach((tab, idx) => {
      m[idx] = tab.section;
    });
    return m;
  }, [tabsDef]);

  React.useEffect(() => {
    // Se o segmento mudou e a aba atual ficou inválida, volta para a primeira.
    if (activeTab >= tabsDef.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabsDef.length]);

  const handleExport = async () => {
    const section = sectionByTab[activeTab];
    if (!section) return;
    if (section === "surveys") return;

    const params = {
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
    };

    try {
      await exportDashboardCsv(
        section,
        params,
        `dashboard_${section}_${startDate.format("YYYYMMDD")}_${endDate.format("YYYYMMDD")}.csv`
      );
    } catch (err) {
      console.error("Erro ao exportar:", err);
      // Mantenho simples (sem Snackbar, para não mexer no resto do layout)
      alert(err?.message || t("dashboardPage.exportFailed"));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        className="tour-dashboard-root"
        sx={{
          padding: 1,
          paddingBottom: '150px !important',
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        <Box
          className="tour-dashboard-header"
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 2 : 0,
            marginBottom: 3,
          }}
        >
          <Typography variant="h4" sx={{ textAlign: isMobile ? "center" : "left" }}>
            {t("dashboardPage.title")}
          </Typography>
          <Box
            className="tour-dashboard-period"
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 2,
              alignItems: isMobile ? "stretch" : "center",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <DatePicker
              label={t("dashboardPage.startDate")}
              value={startDate}
              onChange={handleStartDateChange}
              format="DD/MM/YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={startDate.format("DD/MM/YYYY")}
                  fullWidth={isMobile}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiInputBase-input": { color: "black" },
                  }}
                />
              )}
            />
            <DatePicker
              label={t("dashboardPage.endDate")}
              value={endDate}
              onChange={handleEndDateChange}
              format="DD/MM/YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={endDate.format("DD/MM/YYYY")}
                  fullWidth={isMobile}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiInputBase-input": { color: "black" },
                  }}
                />
              )}
            />
            {!isMobile && (
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExport} // <<< integra Exportar
                disabled={sectionByTab[activeTab] === "surveys"}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
              >
                {t("dashboardPage.export")}
              </Button>
            )}
          </Box>
        </Box>

        <Box>
          {billingSignal ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate("/settings/billing")}>
                  {t("dashboardPage.managePlan", { defaultValue: "Gerenciar plano" })}
                </Button>
              }
            >
              {billingSignal?.payload?.message ||
                t("dashboardPage.billingSignal", {
                  defaultValue: "Detectamos oportunidade de upgrade/add-on com base no uso atual.",
                })}
            </Alert>
          ) : null}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: theme.palette.divider,
              "& .MuiTab-root": {
                color: theme.palette.text.primary,
              },
              "& .Mui-selected": {
                color: theme.palette.primary.main,
              },
            }}
            variant={isMobile ? "scrollable" : "standard"} // Habilita scroll no mobile
            scrollButtons={isMobile ? "auto" : false} // Mostra botões de rolagem no mobile
          >
            {tabsDef.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
            {/* <Tab label="Pesquisas" /> */}
            {/* <Tab label="Bot" /> */}
            {/* <Tab label="Campanhas" /> */}
            {/* <Tab label="Xpay" /> */}
          </Tabs>

          <Box className="tour-dashboard-tab-panel" sx={{ marginTop: 2 }}>
            {tabsDef[activeTab]?.render?.()}
            {/* {activeTab === 7 && <BotAnalytics />} */}
            {/* {activeTab === 8 && <CampaignAnalysis />} */}
            {/* {activeTab === 10 && <Xpay />} */}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Dashboard;
